import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import { BackLink, StatsRow, StatCard, Card, EmptyState } from "../../components/result/Primitives";
import { Button } from "../../components/ui/button";
import { BellRing, CalendarPlus, CreditCard, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (cents) => `€ ${(Number(cents || 0) / 100).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

const STATE_BADGE = {
  trialing: ["In prova", "bg-accent-subtle text-accent-strong"],
  active: ["Attivo", "bg-emerald-100 text-emerald-800"],
  grace: ["Pagamento fallito", "bg-amber-100 text-amber-800"],
  readonly: ["Sola lettura", "bg-red-100 text-red-700"],
  pending: ["Email da verificare", "bg-subtle text-text-secondary"],
};

export default function SuperAdminSubscriptions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyOrg, setBusyOrg] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);

  const [failed, setFailed] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/subscriptions");
      setData(data);
      setFailed(false);
    } catch (e) { setFailed(true); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const extendTrial = async (orgId) => {
    setBusyOrg(orgId);
    try {
      const { data } = await api.post(`/superadmin/subscriptions/${orgId}/extend-trial`, { days: 7 });
      toast.success(`Trial esteso fino al ${fmtDate(data.trial_ends_at)}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyOrg(null); }
  };

  const runReminders = async () => {
    setSendingReminders(true);
    try {
      const { data } = await api.post("/superadmin/subscriptions/run-trial-reminders");
      toast.success(data.sent > 0
        ? `Promemoria inviati a ${data.sent} ${data.sent === 1 ? "organizzazione" : "organizzazioni"}`
        : "Nessun trial in scadenza nei prossimi 2 giorni (o già avvisati)");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingReminders(false); }
  };

  const rows = data?.rows || [];
  const funnel = data?.funnel || {};

  return (
    <div className="space-y-6" data-testid="sa-subscriptions-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <BackLink to="/superadmin/organizations" label="Wattline" />
          <h1 className="text-3xl font-semibold text-text-primary tracking-tight">Abbonamenti</h1>
          <p className="text-base text-text-secondary mt-2">
            Org self-service: stati, consumi del mese e conversioni trial → pagante
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-md border-border h-10" onClick={load} data-testid="sa-subs-reload">
            <RefreshCw size={14} className="mr-1.5" /> Aggiorna
          </Button>
          <Button className="rounded-md h-10" onClick={runReminders} disabled={sendingReminders}
                  data-testid="sa-subs-run-reminders">
            <BellRing size={14} className="mr-1.5" />
            {sendingReminders ? "Invio…" : "Invia promemoria trial"}
          </Button>
        </div>
      </div>

      <StatsRow columns={4}>
        <StatCard label="MRR" value={fmtEur(data?.mrr_cents)} testId="sa-subs-mrr" />
        <StatCard label="Abbonamenti attivi" value={data?.active_now ?? "—"} testId="sa-subs-active" />
        <StatCard label="In prova ora" value={data?.trialing_now ?? "—"} testId="sa-subs-trialing" />
        <StatCard label="Conversione trial → pagante"
                  value={`${funnel.conversion_pct ?? 0}%`}
                  detail={`${funnel.converted ?? 0} su ${funnel.trials ?? 0} trial`}
                  testId="sa-subs-conversion" />
      </StatsRow>

      <Card>
        {loading ? (
          <p className="text-sm text-text-secondary p-4">Caricamento…</p>
        ) : failed ? (
          <LoadErrorBox what="gli abbonamenti" onRetry={load} testId="sa-subs-error" />
        ) : rows.length === 0 ? (
          <EmptyState icon={CreditCard} title="Nessun abbonamento self-service"
                      description="Le org registrate dal sito compariranno qui." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sa-subs-table">
              <thead>
                <tr className="text-left text-xs text-text-muted border-b border-border">
                  <th className="py-2 pr-4 font-medium">Organizzazione</th>
                  <th className="py-2 pr-4 font-medium">Piano</th>
                  <th className="py-2 pr-4 font-medium">Stato</th>
                  <th className="py-2 pr-4 font-medium">Utenti</th>
                  <th className="py-2 pr-4 font-medium">Op. AI</th>
                  <th className="py-2 pr-4 font-medium">Ricerche</th>
                  <th className="py-2 pr-4 font-medium">Scadenza</th>
                  <th className="py-2 font-medium">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const [label, cls] = STATE_BADGE[r.state] || [r.state, "bg-subtle text-text-secondary"];
                  return (
                    <tr key={r.org_id} className="border-b border-border last:border-0"
                        data-testid={`sa-subs-row-${r.org_id}`}>
                      <td className="py-2.5 pr-4">
                        <div className="font-medium text-text-primary">{r.org_name}</div>
                        <div className="text-xs text-text-muted">{r.org_id}</div>
                      </td>
                      <td className="py-2.5 pr-4">
                        {r.plan_name || "—"}
                        <span className="text-xs text-text-muted"> · {r.interval === "yearly" ? "annuale" : "mensile"}</span>
                      </td>
                      <td className="py-2.5 pr-4">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>
                        {r.extended_days > 0 && (
                          <span className="ml-1 text-xs text-text-muted">+{r.extended_days}gg</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4">{r.users_count}/{r.max_users}</td>
                      <td className="py-2.5 pr-4">
                        {r.ai_ops_used === null || r.ai_ops_used === undefined ? "—" : `${r.ai_ops_used}/${r.ai_ops_limit}`}
                      </td>
                      <td className="py-2.5 pr-4">
                        {r.searches_used === null || r.searches_used === undefined ? "—" : `${r.searches_used}/${r.searches_limit}`}
                      </td>
                      <td className="py-2.5 pr-4">
                        {r.state === "trialing" ? `prova fino al ${fmtDate(r.trial_ends_at)}` : fmtDate(r.current_period_end)}
                      </td>
                      <td className="py-2.5">
                        {(r.state === "trialing" || (r.state === "readonly" && r.reason === "trial_expired")) && (
                          <Button variant="outline" size="sm" className="rounded-md border-border h-8"
                                  disabled={busyOrg === r.org_id}
                                  onClick={() => extendTrial(r.org_id)}
                                  data-testid={`sa-subs-extend-${r.org_id}`}>
                            <CalendarPlus size={13} className="mr-1" /> +7 gg
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
